/**
 * List / offer / cancel either / accept an offer, for EXTERNAL (self-custody)
 * wallets — the non-visual half, in the same shape as
 * `features/wallet/external-withdraw-flow.ts`: every browser piece (the
 * connector, the provider, Axios) is injected, so the guarantees that matter
 * on these paths are tested against fake deps rather than inferred from a
 * rendered dialog.
 *
 * WHAT MAKES THIS DIFFERENT FROM WITHDRAW. Withdraw is always exactly one
 * transaction. Here the number of rounds AND the kind of each one are decided
 * by the backend, one `/confirm` response at a time:
 *
 *   place an offer (native)  wrap POL→WPOL · approve WPOL · sign the order
 *   place an offer (WPOL)    approve WPOL · sign the order
 *   list for sale            approve the NFT · sign the order
 *   accept an offer          approve the NFT · settle on Seaport
 *   cancel either            one transaction
 *   create a collection      fund the deployer · nothing (already funded)
 *
 * THAT LAST ROW IS NOT A MARKETPLACE FLOW, and it is here on purpose. It is
 * the same machine — one POST, a step marker, an opaque payload to hand the
 * wallet, a `/confirm` against the id the first response named — so giving it
 * its own copy of the loop, the account/network checks, the send guard, the
 * indexing retry and the wallet panel would be a second implementation of
 * rules that must not drift apart. What it costs is this file's NAME being
 * narrower than its subject: read `finance` here as `backend-driven wallet
 * rounds`, which is what every entry above actually is.
 *
 * So this is a loop over "do what the server just asked, confirm, read the
 * next step", not a fixed sequence — and a round is either an on-chain
 * transaction (gas, a receipt, `{ trx_hash }`) or an off-chain EIP-712
 * signature (no gas, no receipt, `{ signature }`). Which one a step is comes
 * from `STEP_SIGNATURE_KIND`, never from sniffing the payload: a backend that
 * sent both fields, or the wrong one, must fail loudly rather than quietly
 * turn a free signature into a transaction the user pays for.
 *
 * WHAT THIS FILE DOES NOT DO. It never sends the initial POST — `listing` /
 * `offer` / `cancel` / `accept` happens exactly once, before this flow starts,
 * and its response is what decides whether this flow runs at all
 * (`classifyFinanceStep`). It never reconstructs `transaction` or
 * `typed_data`, and it never invents a new listing/offer id: every `/confirm`
 * goes to the id the first response named.
 *
 * The error classes and the account/network/indexing predicates are imported
 * from the withdraw flow rather than re-declared — they encode EIP-1193 facts
 * and one backend convention, neither of which is withdraw-specific, and a
 * second copy would be a second thing to keep in step.
 */

import {
  AccountMismatchError,
  DEFAULT_CONFIRM_RETRY_DELAY_MS,
  DEFAULT_MAX_CONFIRM_ATTEMPTS,
  NetworkMismatchError,
  TransactionFailedError,
  chainIdMatches,
  isRetryableConfirmError,
  isSameAddress,
} from "@/features/wallet/external-withdraw-flow";
import type {
  ConfirmFinanceStepPayload,
  FinanceSignPayload,
  FinanceStepResponse,
} from "./types";

export {
  AccountMismatchError,
  NetworkMismatchError,
  TransactionFailedError,
  isRetryableConfirmError,
};

/** Terminal — the backend is done; nothing is owed by the wallet. */
export const TERMINAL_STEPS = [
  "LISTED",
  "LISTING_CANCELLED",
  "OFFER_PLACED",
  "OFFER_CANCELLED",
  "OFFER_ACCEPTED",
  // Creating a collection from an external wallet whose deployer ALREADY has
  // gas. The backend queued the deploy itself, so this path is identical to
  // the custodial one from here on: wait for the collection to go active.
  "SUBMITTED_TO_CHAIN",
  // THE SAME THING FOR AN NFT, SPELLED DIFFERENTLY BY THE API. Both markers
  // mean the backend has taken the chain work from here; neither asks the
  // wallet for anything. Carried as two entries rather than normalised into
  // one at the call sites, because the string on the wire is the contract and
  // a client that rewrites it is a client that can rewrite it wrongly.
  "SUBMIT_TO_BLOCKCHAIN",
  // The five PLATFORM-wallet actions that submit chain work. Terminal in the sense
  // this table means — the wallet owes nothing — but NOT a result: the chain
  // leg follows and is reported on the user channel. See
  // `chain-settlement.ts`, which is what stops these being read as done.
  "SUBMIT_LISTING",
  "SUBMIT_CANCEL_LISTING",
  "SUBMIT_OFFER",
  "SUBMIT_CANCEL_OFFER",
  "SUBMIT_ACCEPT_OFFER",
] as const;

/**
 * Non-terminal — the user's own wallet owes something. The value is WHAT it
 * owes, and it is the single source of truth for that: `transaction` means an
 * on-chain send (gas, receipt, confirmed with `trx_hash`), `typed_data` means
 * an off-chain EIP-712 order signature (free, no receipt, confirmed with
 * `signature`).
 */
export const STEP_SIGNATURE_KIND = {
  // Listing an NFT: let the marketplace move it, then sign the sale order.
  LISTING_APPROVE_REQUIRED: "transaction",
  LISTING_SIGN_REQUIRED: "typed_data",
  LISTING_CANCEL_REQUIRED: "transaction",
  // Placing an offer: turn native POL into WPOL (skipped when the offer is
  // already in WPOL), let the marketplace spend it, then sign the bid.
  OFFER_WRAP_REQUIRED: "transaction",
  OFFER_APPROVE_REQUIRED: "transaction",
  OFFER_SIGN_REQUIRED: "typed_data",
  OFFER_CANCEL_REQUIRED: "transaction",
  // Accepting an offer: let the marketplace move the NFT, then settle.
  OFFER_ACCEPT_APPROVE_REQUIRED: "transaction",
  OFFER_ACCEPT_REQUIRED: "transaction",
  // Creating a COLLECTION or an NFT from an external wallet: top up the
  // deployer so it
  // can pay for the deploy. THE ODD ONE OUT IN THIS TABLE, and worth knowing
  // before reading its payload — it is a plain NATIVE TRANSFER to an address,
  // not a contract call, and it does not create the collection. The collection
  // row already exists by then (`collection_id` / `nft_id` comes back with this
  // step); this round only pays for its chain work.
  FUND_DEPLOYER: "transaction",
} as const;

export type TerminalStep = (typeof TERMINAL_STEPS)[number];
export type SignRequiredStep = keyof typeof STEP_SIGNATURE_KIND;
export type SignatureKind = (typeof STEP_SIGNATURE_KIND)[SignRequiredStep];

/** Free EIP-712 signature, or a transaction the user pays gas for? */
export function signatureKindOf(step: SignRequiredStep): SignatureKind {
  return STEP_SIGNATURE_KIND[step];
}

export type FinanceStepClassification =
  | { kind: "terminal"; step: TerminalStep }
  | { kind: "sign_required"; step: SignRequiredStep }
  /**
   * No `step` at all. The site's custodial wallet and every pre-external
   * response shape land here, and so does a `/confirm` that simply closed the
   * sequence without restating the terminal marker — all three mean "the
   * backend accepted it and is asking nothing more of the wallet", which is
   * why this is `settled` and not an error.
   */
  | { kind: "settled" }
  /** A marker this client has never seen. Never signed against — see §note. */
  | { kind: "unknown"; step: string };

export function classifyFinanceStep(step: unknown): FinanceStepClassification {
  if (typeof step !== "string" || step.length === 0) return { kind: "settled" };
  const upper = step.toUpperCase();
  if ((TERMINAL_STEPS as readonly string[]).includes(upper)) {
    return { kind: "terminal", step: upper as TerminalStep };
  }
  if (upper in STEP_SIGNATURE_KIND) {
    return { kind: "sign_required", step: upper as SignRequiredStep };
  }
  return { kind: "unknown", step };
}

/**
 * True when the first POST's response means "the wallet has work to do".
 * Everything else — including the custodial wallet's terminal answer — is
 * finished business the existing non-wallet code path already handles.
 */
export function requiresExternalSignature(response: FinanceStepResponse | null | undefined): boolean {
  return classifyFinanceStep(response?.data?.step).kind === "sign_required";
}

/**
 * The id every `/confirm` in the sequence must be posted against.
 *
 * `collection_id` joins the two marketplace ids rather than getting a reader
 * of its own: the rule it encodes — take the id from the FIRST response and
 * post every later round against that one, never a freshly created entity —
 * is the reason this function exists, and it is exactly as load-bearing for a
 * half-funded collection as for a half-signed listing.
 */
export function readStepId(
  response: FinanceStepResponse | null | undefined,
  field: "listing_id" | "offer_id" | "collection_id" | "nft_id",
): number | string | null {
  const value = response?.data?.[field];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.length > 0) return value;
  return null;
}

/**
 * The backend asked for a round but sent nothing for the wallet to act on —
 * no payload at all, or a payload missing the field its step's kind requires
 * (a `*_SIGN_REQUIRED` without `typed_data`, a transaction step without
 * `transaction`). Not retryable by signing: there is nothing to sign, and
 * falling back to "whatever field IS present" would send a transaction where
 * the backend meant a free signature.
 */
export class MissingSignPayloadError extends Error {
  step: string;
  constructor(step: string) {
    super("missing_sign_payload");
    this.name = "MissingSignPayloadError";
    this.step = step;
  }
}

/**
 * The backend answered with a step marker this client does not know. Refusing
 * is deliberate: the alternative is guessing which of "done" and "sign
 * something" it meant, and one of those guesses reports a cancellation or a
 * sale that never happened.
 */
export class UnknownFinanceStepError extends Error {
  step: string;
  constructor(step: string) {
    super("unknown_finance_step");
    this.name = "UnknownFinanceStepError";
    this.step = step;
  }
}

export type FinanceSigningStatus =
  | "validating_wallet_account"
  | "validating_network"
  /** The wallet's Send popup is open — this one costs gas. */
  | "awaiting_wallet_signature"
  /** The wallet's Sign popup is open — free, and nothing goes on-chain. */
  | "awaiting_typed_signature"
  | "transaction_submitted"
  | "waiting_for_receipt"
  | "confirming_backend"
  | "retrying_same_confirmation"
  | "awaiting_next_signature"
  | "done";

export type FinanceSigningResult =
  | {
      outcome: "done";
      /**
       * One per ON-CHAIN round, in order — signature rounds contribute none.
       * Empty for a listing whose approval was already in place, three for a
       * native-currency offer that had to wrap and approve first.
       */
      trxHashes: string[];
      /** The `/confirm` envelope that closed the sequence. */
      lastResponse: FinanceStepResponse;
    }
  /**
   * The last transaction succeeded on-chain and `/confirm` was retried the
   * bounded number of times, but the backend is still indexing it. NOT a
   * failure: the id and hash stay valid and `/confirm` may be retried later
   * with the exact same pair — never by sending the transaction again.
   */
  | {
      outcome: "pending_confirmation";
      trxHashes: string[];
      lastConfirmError: unknown;
    };

export type FinanceSigningDeps = {
  /** The `sign_payload` from the response that started this — round 1's transaction. */
  signPayload: FinanceSignPayload;
  /** The step marker that came with it, for error messages and refusal. */
  step: SignRequiredStep;
  /** Live re-read of the wallet's current selection — never a cached value. */
  readAccount: () => Promise<{ address: string; chainId: string }>;
  /** Throws on rejection/failure. The caller re-reads the chain to verify. */
  switchNetwork: (chainId: number) => Promise<void>;
  /**
   * `eth_sendTransaction` with the payload's own transaction object. Called at
   * most ONCE PER STEP, and `context.step` — not the round index — is what a
   * caller must key its "already sent" guard on: a resumed attempt starts its
   * own round 1 at whatever step the backend is now on, so a round-keyed
   * guard would hand back the wrong hash, or miss and send a second
   * transaction the user pays for.
   */
  sendTransaction: (
    transaction: Record<string, unknown>,
    context: { round: number; step: SignRequiredStep },
  ) => Promise<string>;
  /**
   * `eth_signTypedData_v4` with the payload's own typed data. Free and
   * off-chain — no receipt follows it. Guarded per step for the same reason,
   * though the cost of a duplicate here is a redundant popup, not gas.
   */
  signTypedData: (
    typedData: Record<string, unknown>,
    context: { round: number; step: SignRequiredStep },
  ) => Promise<string>;
  /** Resolves once the tx has a final receipt. `status`: 1 success, 0 revert, null if indeterminate. */
  waitForReceipt: (trxHash: string) => Promise<{ status: number | null }>;
  /**
   * POSTs the step's own `/confirm`, against the id from the first response,
   * with whatever this round produced — `{ trx_hash }` or `{ signature }`.
   */
  confirmStep: (payload: ConfirmFinanceStepPayload) => Promise<FinanceStepResponse>;
  /**
   * Fired the moment a round is confirmed and the backend names the next one.
   * The caller uses it to move its own idea of "where this listing/offer is"
   * forward, so that if a later round is rejected, resuming starts at the
   * step the backend is actually on — never by replaying a round that is
   * already paid for and confirmed.
   */
  onStepAdvanced?: (next: { step: SignRequiredStep; signPayload: FinanceSignPayload }) => void;
  onStatus?: (status: FinanceSigningStatus) => void;
  /** Injectable for tests — real callers omit and get a real timer. */
  delay?: (ms: number) => Promise<void>;
  maxConfirmAttempts?: number;
  confirmRetryDelayMs?: number;
  /** Guards against a server that keeps asking for rounds. */
  maxRounds?: number;
};

/**
 * Three is the longest real sequence — an offer in a native currency (wrap,
 * approve, sign). Five leaves headroom for a step this client has not met yet
 * without letting a looping server ask the user for transactions forever.
 */
export const DEFAULT_MAX_ROUNDS = 5;

/**
 * Runs the rounds the backend asks for, in order. Each round: validate the
 * connected account, validate (and if needed switch) the network, then do
 * EXACTLY ONE of
 *
 *   transaction — send it once, wait for a real receipt, confirm `{ trx_hash }`
 *   typed_data  — sign it once, confirm `{ signature }` (no gas, no receipt)
 *
 * retrying `/confirm` alone, with the same id and payload, for the documented
 * indexing delay, and repeating while the confirm response asks for another
 * round.
 *
 * Throws `AccountMismatchError` / `NetworkMismatchError` /
 * `TransactionFailedError` / `MissingSignPayloadError` /
 * `UnknownFinanceStepError` for the hard-stop cases; anything the wallet
 * itself throws (a rejection, an RPC error) propagates unchanged — this
 * function never invents a friendlier error in its place, and a rejection
 * leaves the listing/offer id untouched, on the step it was already on, so
 * the caller can resume against it.
 */
export async function runExternalFinanceSigning(
  deps: FinanceSigningDeps,
): Promise<FinanceSigningResult> {
  const delay = deps.delay ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const maxConfirmAttempts = deps.maxConfirmAttempts ?? DEFAULT_MAX_CONFIRM_ATTEMPTS;
  const confirmRetryDelayMs = deps.confirmRetryDelayMs ?? DEFAULT_CONFIRM_RETRY_DELAY_MS;
  const maxRounds = deps.maxRounds ?? DEFAULT_MAX_ROUNDS;

  const trxHashes: string[] = [];
  let signPayload = deps.signPayload;
  let step: SignRequiredStep = deps.step;

  for (let round = 1; round <= maxRounds; round += 1) {
    deps.onStatus?.("validating_wallet_account");
    let current = await deps.readAccount();
    if (!isSameAddress(current.address, signPayload.from)) {
      throw new AccountMismatchError(signPayload.from, current.address);
    }

    deps.onStatus?.("validating_network");
    if (!chainIdMatches(current.chainId, signPayload.chain_id)) {
      await deps.switchNetwork(signPayload.chain_id);
      current = await deps.readAccount();
      if (!chainIdMatches(current.chainId, signPayload.chain_id)) {
        throw new NetworkMismatchError(signPayload.chain_id);
      }
      // The wallet's account picker sits in the same UI as its network
      // switcher; re-asserting here catches a user who changed both.
      if (!isSameAddress(current.address, signPayload.from)) {
        throw new AccountMismatchError(signPayload.from, current.address);
      }
    }

    // The step decides which of the two this is. A `*_SIGN_REQUIRED` that
    // happens to carry a `transaction` is a backend bug, and sending it would
    // charge the user gas for something the API said was free — so the field
    // the step calls for must be there, or the round stops.
    let confirmPayload: ConfirmFinanceStepPayload;
    if (signatureKindOf(step) === "typed_data") {
      if (!signPayload.typed_data) throw new MissingSignPayloadError(step);
      deps.onStatus?.("awaiting_typed_signature");
      const signature = await deps.signTypedData(signPayload.typed_data, { round, step });
      confirmPayload = { signature };
    } else {
      if (!signPayload.transaction) throw new MissingSignPayloadError(step);
      deps.onStatus?.("awaiting_wallet_signature");
      const trxHash = await deps.sendTransaction(signPayload.transaction, { round, step });
      trxHashes.push(trxHash);
      deps.onStatus?.("transaction_submitted");

      deps.onStatus?.("waiting_for_receipt");
      const receipt = await deps.waitForReceipt(trxHash);
      if (receipt.status !== 1) {
        throw new TransactionFailedError(trxHash, receipt.status);
      }
      confirmPayload = { trx_hash: trxHash };
    }

    deps.onStatus?.("confirming_backend");
    let response: FinanceStepResponse | null = null;
    let lastError: unknown = null;
    for (let attempt = 1; attempt <= maxConfirmAttempts; attempt += 1) {
      try {
        response = await deps.confirmStep(confirmPayload);
        lastError = null;
        break;
      } catch (error) {
        lastError = error;
        if (!isRetryableConfirmError(error) || attempt === maxConfirmAttempts) break;
        deps.onStatus?.("retrying_same_confirmation");
        await delay(confirmRetryDelayMs);
      }
    }

    if (!response) {
      if (isRetryableConfirmError(lastError)) {
        return { outcome: "pending_confirmation", trxHashes, lastConfirmError: lastError };
      }
      throw lastError;
    }

    const next = classifyFinanceStep(response.data?.step);
    if (next.kind === "unknown") throw new UnknownFinanceStepError(next.step);
    if (next.kind !== "sign_required") {
      deps.onStatus?.("done");
      return { outcome: "done", trxHashes, lastResponse: response };
    }

    const nextPayload = response.data?.sign_payload;
    if (!nextPayload) throw new MissingSignPayloadError(next.step);
    signPayload = nextPayload;
    step = next.step;
    // Announced BEFORE the next round runs, so a rejection there resumes from
    // here rather than from the top of a sequence whose earlier rounds are
    // already on-chain.
    deps.onStepAdvanced?.({ step, signPayload });
    deps.onStatus?.("awaiting_next_signature");
  }

  // Every round was spent and the server still wants a signature. Reported as
  // the unfinished sequence it is, rather than sending an unbounded number of
  // transactions the user pays for.
  throw new UnknownFinanceStepError(step);
}
